import {
	type ReactElement,
	type RefObject,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import styled, { ThemeProvider } from "styled-components";
import { FilterName, FilterSelector } from "../../state/filter";
import {
	IsInPlayWrightTest,
	PageSizeWithPaddingSelector,
	StickyHeaderHeightAtom,
	TapIdContainers,
	pageHasWidescreenState,
} from "../../state/page";
import { AllLoaded, NumberOfPages } from "../../state/selectors";
import {
	ConstrainDimensionsSelector,
	IsInAuthor,
	IsPreviewGenerator,
	TapDirection,
} from "../../state/ui";

import { useRecoilValue } from "recoil";
// @ts-expect-error: No types for this package
import smartquotes from "smartquotes";
import type { ElementsProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useAnimation from "../../lib/animations/animationsHook";
import { displayElementsCssRules } from "../../lib/cssTemplates";
import { NavigationType } from "../../state/navigation";
import { PageTimelineContext, PositionContext } from "./DisplayElements";
import RenderSplitPage from "./RenderSplitPage";
import SafariWrapper from "./SafariWrapper";

export const StyledSplitPageElements = styled.section<
	ElementsProps & { $hasWidescreen?: boolean; $baseFontSizeRaw: number }
>`
  display: flex;
  flex-direction: column;
  height: ${(props) => (props.$navigationType === "scroll" ? "auto" : "100%")};
  justify-self: center;
  overflow-y: ${(props) => (props.$isInLandscape || props.$navigationType === "scroll" ? "hidden" : "visible")};
  width: ${(props) => props.$width}px;
  padding: 0;
  ${(props) =>
		`${displayElementsCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			fonts: props.$fonts,
			theme: props.theme,
			isOverflowing: props.$isOverflowing ? props.$isOverflowing : false,
			isInLandscape: props.$isInLandscape ? props.$isInLandscape : false,
			stickyHeaderHeight: props.$stickyHeaderHeight,
		})}`};
  /* Frame padding is handled by PageSizeWithPaddingSelector — zero out CSS padding to avoid double-application */
  padding-inline: 0;
  padding-block: 0;

   ${(props) =>
			props.$navigationType === "scroll" ||
			props.$isPreviewer ||
			props.$isInPlayWrightTest
				? ""
				: "position: absolute;"}
  ${(props) => props.$hasWidescreen && "padding: 0;"}

  /* Hide focus ring - this element receives programmatic focus for screen readers only */
  &:focus {
    outline: none;
  }
`;

function splitArrayByIndices(
	arr: ReactElement<ElementProps>[],
	pageSplits: number[],
) {
	const subArrays = [];
	let startIndex = 0;
	for (const index of pageSplits) {
		subArrays.push(arr.slice(startIndex, index));
		startIndex = index;
	}
	subArrays.push(arr.slice(startIndex));
	return subArrays;
}

function DisplaySplitPage({
	pageElementsToDisplay,
	pageIndex,
	pageSplits,
	scrollerRef,
	"aria-hidden": ariaHidden,
}: {
	pageIndex: number;
	pageElementsToDisplay: ReactElement<ElementProps>[];
	pageSplits: number[];
	scrollerRef?: RefObject<HTMLDivElement>;
	"aria-hidden"?: boolean;
}) {
	const pageSize = useRecoilValue(PageSizeWithPaddingSelector);
	const baseFontSize = pageSize.baseFontSize;
	const direction = useRecoilValue(TapDirection);
	const filterName = useRecoilValue(FilterName);
	const filter = useRecoilValue(FilterSelector(filterName));
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isInLandscape = pageSize.landscape;
	const isInPlayWrightTest = useRecoilValue(IsInPlayWrightTest);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const navigationType = useRecoilValue(NavigationType);
	const numberOfPages = useRecoilValue(NumberOfPages);
	const overflowRef = useRef<HTMLDivElement>(null);
	const pageSections = splitArrayByIndices(pageElementsToDisplay, pageSplits);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const position = useContext(PositionContext);
	const allLoaded = useRecoilValue(AllLoaded); // useful for measuring images
	const [gridHasRendered, setGridHasRendered] = useState(false); // this is smelly. Pass it thru RenderSplitPage to RenderGrid
	const stickyHeaderHeight = useRecoilValue(StickyHeaderHeightAtom);
	const isInAuthor = useRecoilValue(IsInAuthor);

	const tapIdContainers = useRecoilValue(TapIdContainers(pageIndex));

	const pageTimeline = useAnimation({
		direction,
		elRef: overflowRef,
		filter,
		isInAuthor,
		isInLandscape: pageSize.landscape,
		isInPlayWrightTest,
		isOverflowing,
		isPreviewer,
		navigationType,
		numberOfPages,
		onePercent: {
			width: pageSize.onePercentWidth,
			height: pageSize.onePercentHeight,
		},
		pageIndex,
		position,
		tapIdContainers,
		scrollerRef,
	});
	const pageHasWidescreen = useRecoilValue(pageHasWidescreenState(pageIndex));

	// add smartquotes to each element when the displayed content changes
	useEffect(() => {
		const styledSplitPageElements = document.getElementsByClassName(
			StyledSplitPageElements.styledComponentId,
		);
		for (const el of Array.from(styledSplitPageElements)) {
			smartquotes(el);
		}
	}, [pageElementsToDisplay, pageIndex]);

	// check if content is overflowing on mobile when using middleOut layout
	useLayoutEffect(() => {
		if (overflowRef.current) {
			const displayChildrenScrollHeight = overflowRef.current.scrollHeight;
			const displayChildrenHeight = overflowRef.current.clientHeight;
			// const ef = overflowRef.current.offsetHeight;
			const overflowing = displayChildrenScrollHeight > displayChildrenHeight;
			setIsOverflowing(overflowing);
			// //console.log("displayChildrenScrollHeight: ", displayChildrenScrollHeight);
			// //console.log("displayChildrenHeight:", displayChildrenHeight);
			// //console.log("ef: ", ef);
			// //console.log(overflowing);
			// //console.log("--------------");
		}
	}, [pageSize, pageElementsToDisplay, pageIndex, allLoaded, gridHasRendered]);

	return (
		<SafariWrapper
			variant="split-page"
			$hasWidescreen={pageHasWidescreen}
			aria-hidden={ariaHidden}
		>
			<PageTimelineContext.Provider value={pageTimeline}>
				<ThemeProvider theme={filter}>
					<StyledSplitPageElements
						tabIndex={-1}
						$isPreviewer={isPreviewer}
						$isOverflowing={isOverflowing}
						$baseFontSize={baseFontSize}
						$baseFontSizeRaw={pageSize.baseFontSizeRaw}
						$height={pageSize.height}
						$constrainDimensions={constrainDimensions}
						$isInLandscape={isInLandscape}
						$isInPlayWrightTest={isInPlayWrightTest}
						$navigationType={navigationType}
						$width={pageSize.width}
						className={`display-split-page ${position} ${isPreviewer ? "previewer" : ""}`}
						data-testid="DisplaySplitPage"
						ref={overflowRef}
						$stickyHeaderHeight={stickyHeaderHeight}
						key={`splitPage-${pageIndex}}`}
						id={`display-elements-children-${pageIndex}`}
						$hasWidescreen={pageHasWidescreen}
					>
						<RenderSplitPage
							pageSections={pageSections}
							setGridHasRenderedCallback={setGridHasRendered}
						/>
					</StyledSplitPageElements>
				</ThemeProvider>
			</PageTimelineContext.Provider>
		</SafariWrapper>
	);
}

export default DisplaySplitPage;
